import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { computeStats, matchesNomenclatureFilters, buildExportPayload, exportNomenclaturesXlsx } from './nomenclatureHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useNomenclature() {
  const { getAuthHeader } = useAuth();
  
  // États principaux
  const [activeMainTab, setActiveMainTab] = useState('nomenclatures');
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [nomenclatures, setNomenclatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dispositifs, setDispositifs] = useState([]);
  const [estimateurs, setEstimateurs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtres et recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedDispositif, setSelectedDispositif] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  // V36.16 — recherche dans la liste des dispositifs applicables (modale d'édition)
  const [dispositifSearch, setDispositifSearch] = useState('');
  // V36.17 — recherche locale dans la liste des estimateurs liés (modale d'édition)
  const [estimateurSearch, setEstimateurSearch] = useState('');
  
  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [assignDispositifDialogOpen, setAssignDispositifDialogOpen] = useState(false);
  // V36.19 — Lier en masse (Dispositifs + Estimateurs + modes add/replace/remove)
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
  // V36.20 — Le BulkActionsMenu pré-active une section quand on clique sur "Lier dispositifs/estimateurs"
  const [bulkLinkInitialTab, setBulkLinkInitialTab] = useState(null);
  const [assignEstimateurDialogOpen, setAssignEstimateurDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  
  // Élément en cours d'édition
  const [currentItem, setCurrentItem] = useState(null);
  
  // Formulaire
  const [formData, setFormData] = useState({
    collection_id: '',
    category: '',
    code: '',
    name: '',
    definition: '',
    exclusions: '',
    status: 'active',
    dispositifs: [],
    estimateurs: [],
    applicable_devenir_membre: true,
    devenir_membre_status: 'active'
  });
  
  // Statistiques
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
    archived: 0,
    byCategory: {},
    byDispositif: {}
  });
  
  const fileInputRef = useRef(null);

  // ==================== CHARGEMENT DES DONNÉES ====================
  
  useEffect(() => {
    fetchCollections();
    fetchDispositifs();
    fetchEstimateurs();
  }, []);

  useEffect(() => {
    if (selectedCollection) {
      fetchCategories();
      fetchNomenclatures();
    }
  }, [selectedCollection]);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API}/nomenclature-collections`, {
        headers: getAuthHeader()
      });
      setCollections(response.data);
      const activeCollection = response.data.find(c => c.is_active);
      if (activeCollection && !selectedCollection) {
        setSelectedCollection(activeCollection);
        setFormData(prev => ({ ...prev, collection_id: activeCollection.id }));
      }
    } catch {
      toast.error('Erreur lors du chargement des collections');
    }
  };

  const fetchCategories = async () => {
    if (!selectedCollection) return;
    try {
      const response = await axios.get(
        `${API}/categories?collection_id=${selectedCollection.id}&active_only=true`, 
        { headers: getAuthHeader() }
      );
      setCategories(response.data);
    } catch {
    }
  };

  const fetchNomenclatures = async () => {
    if (!selectedCollection) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/nomenclatures?collection_id=${selectedCollection.id}${showArchived ? '&include_archived=true' : ''}`, 
        { headers: getAuthHeader() }
      );
      setNomenclatures(response.data);
      calculateStats(response.data);
    } catch {
      toast.error('Erreur lors du chargement des nomenclatures');
    } finally {
      setLoading(false);
    }
  };

  const fetchDispositifs = async () => {
    try {
      const response = await axios.get(`${API}/dispositifs`, {
        headers: getAuthHeader()
      });
      setDispositifs(response.data);
    } catch {
    }
  };

  const fetchEstimateurs = async () => {
    try {
      const response = await axios.get(`${API}/estimateurs?active_only=true`, {
        headers: getAuthHeader()
      });
      setEstimateurs(response.data);
    } catch {
    }
  };

  // ==================== STATISTIQUES ====================
  
  const calculateStats = (data) => {
    setStats(computeStats(data));
  };

  // ==================== FILTRAGE ====================
  
  const filteredNomenclatures = nomenclatures.filter(n =>
    matchesNomenclatureFilters(n, { searchTerm, selectedCategory, selectedStatus, selectedDispositif })
  );

  // ==================== ACTIONS CRUD ====================
  
  const handleCreate = async () => {
    try {
      await axios.post(
        `${API}/nomenclatures`,
        {
          ...formData,
          collection_id: selectedCollection?.id || formData.collection_id
        },
        { headers: getAuthHeader() }
      );
      toast.success('Nomenclature créée avec succès');
      setCreateDialogOpen(false);
      resetForm();
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de la création');
    }
  };

  const handleUpdate = async () => {
    if (!currentItem) return;
    try {
      await axios.put(
        `${API}/nomenclatures/${currentItem.id}`,
        formData,
        { headers: getAuthHeader() }
      );
      toast.success('Nomenclature mise à jour');
      setEditDialogOpen(false);
      resetForm();
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette nomenclature ?')) return;
    try {
      await axios.delete(`${API}/nomenclatures/${id}`, { headers: getAuthHeader() });
      toast.success('Nomenclature supprimée');
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Suppression en masse
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error('Aucune nomenclature sélectionnée');
      return;
    }
    
    const confirmMessage = `ATTENTION : Vous êtes sur le point de SUPPRIMER DÉFINITIVEMENT ${selectedIds.size} nomenclature(s).\n\nCette action est IRRÉVERSIBLE.\n\nÊtes-vous absolument sûr de vouloir continuer ?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    // Double confirmation pour suppression en masse
    if (!window.confirm(`Confirmez une dernière fois : supprimer ${selectedIds.size} nomenclature(s) ?`)) return;
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const id of selectedIds) {
      try {
        await axios.delete(`${API}/nomenclatures/${id}`, { headers: getAuthHeader() });
        successCount++;
      } catch {
        errorCount++;
      }
    }
    
    setLoading(false);
    setSelectedIds(new Set());
    setSelectionMode(false);
    
    if (errorCount === 0) {
      toast.success(`${successCount} nomenclature(s) supprimée(s) avec succès`);
    } else {
      toast.warning(`${successCount} supprimée(s), ${errorCount} erreur(s)`);
    }
    
    fetchNomenclatures();
  };

  // Suppression de TOUTES les nomenclatures de la collection
  const handleDeleteAll = async () => {
    const total = filteredNomenclatures.length;
    if (total === 0) {
      toast.error('Aucune nomenclature à supprimer');
      return;
    }
    
    const confirmMessage = `⚠️ SUPPRESSION TOTALE ⚠️\n\nVous êtes sur le point de SUPPRIMER DÉFINITIVEMENT TOUTES les ${total} nomenclatures affichées.\n\nCette action est IRRÉVERSIBLE.\n\nTapez "SUPPRIMER" pour confirmer :`;
    
    const confirmation = window.prompt(confirmMessage);
    if (confirmation !== 'SUPPRIMER') {
      toast.info('Suppression annulée');
      return;
    }
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const item of filteredNomenclatures) {
      try {
        await axios.delete(`${API}/nomenclatures/${item.id}`, { headers: getAuthHeader() });
        successCount++;
      } catch {
        errorCount++;
      }
    }
    
    setLoading(false);
    setSelectedIds(new Set());
    
    if (errorCount === 0) {
      toast.success(`${successCount} nomenclature(s) supprimée(s) avec succès`);
    } else {
      toast.warning(`${successCount} supprimée(s), ${errorCount} erreur(s)`);
    }
    
    fetchNomenclatures();
  };

  const handleArchive = async (id) => {
    try {
      await axios.patch(
        `${API}/nomenclatures/${id}/archive`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Nomenclature archivée');
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de l\'archivage');
    }
  };

  // Archivage en masse des nomenclatures sélectionnées
  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) {
      toast.error('Aucune nomenclature sélectionnée');
      return;
    }
    
    if (!window.confirm(`Archiver ${selectedIds.size} nomenclature(s) ?`)) return;
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const id of selectedIds) {
      try {
        await axios.patch(`${API}/nomenclatures/${id}/archive`, {}, { headers: getAuthHeader() });
        successCount++;
      } catch {
        errorCount++;
      }
    }
    
    setLoading(false);
    setSelectedIds(new Set());
    setSelectionMode(false);
    
    if (errorCount === 0) {
      toast.success(`${successCount} nomenclature(s) archivée(s)`);
    } else {
      toast.warning(`${successCount} archivée(s), ${errorCount} erreur(s)`);
    }
    
    fetchNomenclatures();
  };

  // Archiver TOUTES les nomenclatures affichées
  const handleArchiveAll = async () => {
    const total = filteredNomenclatures.length;
    if (total === 0) {
      toast.error('Aucune nomenclature à archiver');
      return;
    }
    
    if (!window.confirm(`Archiver les ${total} nomenclatures affichées ?`)) return;
    
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const item of filteredNomenclatures) {
      try {
        await axios.patch(`${API}/nomenclatures/${item.id}/archive`, {}, { headers: getAuthHeader() });
        successCount++;
      } catch {
        errorCount++;
      }
    }
    
    setLoading(false);
    setSelectedIds(new Set());
    
    if (errorCount === 0) {
      toast.success(`${successCount} nomenclature(s) archivée(s)`);
    } else {
      toast.warning(`${successCount} archivée(s), ${errorCount} erreur(s)`);
    }
    
    fetchNomenclatures();
  };

  const handleDuplicate = async (item) => {
    try {
      await axios.post(
        `${API}/nomenclatures`,
        {
          ...item,
          id: undefined,
          code: `${item.code}_COPY`,
          name: `${item.name} (copie)`
        },
        { headers: getAuthHeader() }
      );
      toast.success('Nomenclature dupliquée');
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/nomenclatures/${id}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      toast.success('Statut mis à jour');
      fetchNomenclatures();
    } catch {
      toast.error('Erreur lors du changement de statut');
    }
  };

  // ==================== IMPORT / EXPORT ====================
  
  const handleExport = (format, sheetOptions) => {
    const dataToExport = selectionMode && selectedIds.size > 0
      ? filteredNomenclatures.filter(n => selectedIds.has(n.id))
      : filteredNomenclatures;

    // Excel : classeur multi-onglets (Toutes + par catégorie + par dispositif)
    if (format === 'excel') {
      exportNomenclaturesXlsx(dataToExport, selectedCollection?.name, sheetOptions || {});
      toast.success('Export EXCEL multi-onglets réussi !');
      setExportDialogOpen(false);
      return;
    }

    const { content, filename, type } = buildExportPayload(format, dataToExport, selectedCollection?.name);

    const blob = new Blob(['\uFEFF' + content], { type });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Export ${format.toUpperCase()} réussi !`);
    setExportDialogOpen(false);
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target?.result;
        let importedData;
        
        if (file.name.endsWith('.json')) {
          importedData = JSON.parse(content);
        } else if (file.name.endsWith('.csv')) {
          // Parse CSV
          const lines = content.split('\n');
          const _headers = lines[0].split(';');
          importedData = {
            nomenclatures: lines.slice(1).map(line => {
              const values = line.split(';').map(v => v.replace(/"/g, ''));
              return {
                code: values[0],
                name: values[1],
                category: values[2],
                definition: values[3],
                exclusions: values[4],
                status: values[5],
                dispositifs: values[6] ? values[6].split('; ') : []
              };
            }).filter(n => n.code)
          };
        }
        
        // Envoyer au backend
        await axios.post(
          `${API}/nomenclatures/import-json`,
          {
            nomenclatures: importedData.nomenclatures,
            collection_id: selectedCollection?.id
          },
          { headers: getAuthHeader() }
        );
        
        toast.success(`${importedData.nomenclatures?.length || 0} nomenclatures importées`);
        fetchNomenclatures();
        setImportDialogOpen(false);
      };
      reader.readAsText(file);
    } catch {
      toast.error('Erreur lors de l\'import');
    }
  };

  // ==================== ASSIGNATIONS ====================
  
  const handleAssignDispositifs = async (nomenclatureIds, dispositifIds) => {
    try {
      await axios.post(
        `${API}/nomenclatures/assign-dispositifs`,
        { nomenclature_ids: nomenclatureIds, dispositif_ids: dispositifIds },
        { headers: getAuthHeader() }
      );
      toast.success('Dispositifs assignés avec succès');
      fetchNomenclatures();
      setAssignDispositifDialogOpen(false);
    } catch {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const handleAssignEstimateurs = async (nomenclatureIds, estimateurIds) => {
    try {
      await axios.post(
        `${API}/nomenclatures/assign-estimateurs`,
        { nomenclature_ids: nomenclatureIds, estimateur_ids: estimateurIds },
        { headers: getAuthHeader() }
      );
      toast.success('Estimateurs assignés avec succès');
      fetchNomenclatures();
      setAssignEstimateurDialogOpen(false);
    } catch {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  // ==================== HELPERS ====================
  
  const resetForm = () => {
    setFormData({
      collection_id: selectedCollection?.id || '',
      category: categories[0]?.name || '',
      code: '',
      name: '',
      definition: '',
      exclusions: '',
      status: 'active',
      dispositifs: [],
      estimateurs: [],
      applicable_devenir_membre: true,
      devenir_membre_status: 'active'
    });
    setCurrentItem(null);
  };

  const openEditDialog = (item) => {
    setCurrentItem(item);
    setFormData({
      collection_id: item.collection_id || selectedCollection?.id,
      category: item.category || '',
      code: item.code || '',
      name: item.name || '',
      definition: item.definition || '',
      exclusions: item.exclusions || '',
      status: item.status || (item.is_active ? 'active' : 'inactive'),
      dispositifs: item.dispositifs || [],
      estimateurs: item.estimateurs || [],
      applicable_devenir_membre: item.applicable_devenir_membre !== false,
      devenir_membre_status: item.devenir_membre_status || 'active'
    });
    setEditDialogOpen(true);
  };

  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === filteredNomenclatures.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredNomenclatures.map(n => n.id)));
    }
  };

  return { activeMainTab, setActiveMainTab, collections, setCollections, selectedCollection, setSelectedCollection, nomenclatures, setNomenclatures, categories, setCategories, dispositifs, setDispositifs, estimateurs, setEstimateurs, loading, setLoading, searchTerm, setSearchTerm, selectedCategory, setSelectedCategory, selectedStatus, setSelectedStatus, selectedDispositif, setSelectedDispositif, showArchived, setShowArchived, dispositifSearch, setDispositifSearch, estimateurSearch, setEstimateurSearch, selectedIds, setSelectedIds, selectionMode, setSelectionMode, createDialogOpen, setCreateDialogOpen, editDialogOpen, setEditDialogOpen, viewDialogOpen, setViewDialogOpen, importDialogOpen, setImportDialogOpen, exportDialogOpen, setExportDialogOpen, assignDispositifDialogOpen, setAssignDispositifDialogOpen, bulkLinkOpen, setBulkLinkOpen, bulkLinkInitialTab, setBulkLinkInitialTab, assignEstimateurDialogOpen, setAssignEstimateurDialogOpen, statsDialogOpen, setStatsDialogOpen, currentItem, setCurrentItem, formData, setFormData, stats, setStats, fileInputRef, filteredNomenclatures, fetchCollections, fetchCategories, fetchNomenclatures, fetchDispositifs, fetchEstimateurs, calculateStats, handleCreate, handleUpdate, handleDelete, handleBulkDelete, handleDeleteAll, handleArchive, handleBulkArchive, handleArchiveAll, handleDuplicate, handleStatusChange, handleExport, handleImport, handleAssignDispositifs, handleAssignEstimateurs, resetForm, openEditDialog, toggleSelection, selectAll };
}
